import { Flex, Modal, Popover, Upload, message } from 'antd';
import type { UploadRequestOption } from 'rc-upload/lib/interface';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAdminCompanyFileController_create } from '@api-client/generated/AdminCompanyFileController/create';
import { useAdminCompanyFileController_delete } from '@api-client/generated/AdminCompanyFileController/delete';
import { useAdminCompanyFileController_findAll } from '@api-client/generated/AdminCompanyFileController/findAll';
import { useAdminCompanyFileController_updateOneById } from '@api-client/generated/AdminCompanyFileController/updateOneById';
import { Schemas } from '@api-client/generated/types';
import { IconEdit, IconMoreVertical, IconTrash, IconUpload } from '@assets';
import { Card, Loader } from '@components';
import {
  EmptyDocumentPreview,
  FileViewer,
  SearchControl,
  UploadFileItem,
} from '@entities';
import { useCurrentCompanyId } from '@hooks';
import {
  isPreviewPending,
  isPreviewReady,
  isRenderableImageMimeType,
  resolvePreviewSrc,
} from '@utils';

import { getDocumentTypeLabel } from '../documentType';
import * as S from './styled';
import { useFileUpload } from './useFileUpload';

type LegalContentProps = {
  title?: ReactNode;
  externalCompanyId?: string;
};

// While a HEIC→JPEG preview is still being generated the list is re-fetched on
// this interval, so a `pending → ready` transition reaches the UI without a
// manual page reload. Polling stops once nothing is pending.
const PREVIEW_POLL_INTERVAL_MS = 3000;

// Merge just the server's preview fields (+ refreshed signed url) into a locally
// held file, preserving optimistic edits (name, ordering, additions). Returns the
// same reference when nothing changed so the reconcile effect never triggers a
// needless re-render while polling.
const withFreshPreview = (
  local: Schemas.CompanyFile,
  fresh: Schemas.CompanyFile | undefined
): Schemas.CompanyFile =>
  !fresh ||
  (local.previewStatus === fresh.previewStatus &&
    local.previewUrl === fresh.previewUrl &&
    local.url === fresh.url)
    ? local
    : {
        ...local,
        previewStatus: fresh.previewStatus,
        previewUrl: fresh.previewUrl,
        url: fresh.url,
      };

const LegalContent: FC<LegalContentProps> = ({ externalCompanyId, title }) => {
  const accountCompanyId = useCurrentCompanyId();
  const { id: documentId } = useParams();
  const navigate = useNavigate();

  const companyId = externalCompanyId || accountCompanyId;

  const { mutate: createFile, isPending: createFileLoading } =
    useAdminCompanyFileController_create();
  const { mutate: updateFile, isPending: updateFileLoading } =
    useAdminCompanyFileController_updateOneById();
  const { mutate: deleteFile, isPending: deleteFileLoading } =
    useAdminCompanyFileController_delete();

  const [selectedFile, setSelectedFile] = useState<Schemas.CompanyFile | null>(
    null
  );
  const { uploadStatus } = useFileUpload(companyId!);
  const [files, setFiles] = useState<Schemas.CompanyFile[]>([]);
  const [changingFileId, setChangingFileId] = useState('');
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [previewError, setPreviewError] = useState(false);
  const ref = useRef(null);

  // Drive polling off the local mirror, not the query's own data: an optimistic
  // upload lands in `files` (with previewStatus=pending) before it appears in a
  // fetched list, and mutations here don't invalidate the query. `files` stays in
  // sync with the server via the reconcile effect below, so this stops once the
  // last preview turns ready/failed.
  const hasPendingPreview = files.some(isPreviewPending);

  const { data: queryFiles, isPending: getFilesLoading } =
    useAdminCompanyFileController_findAll({
      params: {
        companyId: companyId!,
        type: 'legal',
        personId: companyId!,
      },
      config: {
        refetchInterval: hasPendingPreview ? PREVIEW_POLL_INTERVAL_MS : false,
      },
    });

  useEffect(
    () => {
      if (queryFiles?.length) {
        setFiles(queryFiles.sort((a, b) => a.name.localeCompare(b.name)));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getFilesLoading]
  );

  // Reconcile the server's preview fields into the locally held list + selection
  // on every fetch (incl. background polls), so a freshly generated HEIC preview
  // shows without a reload. Only preview fields are merged — names, ordering and
  // optimistic additions/removals stay local (a poll must not resurrect a
  // just-deleted file or revert an in-flight rename).
  useEffect(() => {
    if (!queryFiles) return;
    const byId = new Map(queryFiles.map((file) => [file.id, file]));
    setFiles((prev) => {
      let changed = false;
      const next = prev.map((file) => {
        const merged = withFreshPreview(file, byId.get(file.id));
        if (merged !== file) changed = true;
        return merged;
      });
      return changed ? next : prev;
    });
    setSelectedFile((prev) =>
      prev ? withFreshPreview(prev, byId.get(prev.id)) : prev
    );
  }, [queryFiles]);

  // Keep the selection in sync with the deeplink (`/companies/:companyId/legal/:id`):
  // whenever `:id` resolves to a file, select it — including when it changes under
  // Back/Forward between legal URLs, not just on first load. When there's no match
  // (bare route, stale/deleted id) fall back to the first file only if nothing is
  // selected yet, so a missing id degrades to the default rather than clearing the
  // current preview.
  useEffect(
    () => {
      if (!files.length || searchValue) return;
      const deeplinked = documentId
        ? files.find((file) => file.id === documentId)
        : undefined;
      if (deeplinked) {
        if (deeplinked.id !== selectedFile?.id) setSelectedFile(deeplinked);
      } else if (!selectedFile) {
        setSelectedFile(files[0]);
      }
    },
    [files, selectedFile, documentId, searchValue]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (changingFileId && changingFileId !== selectedFile?.id) {
      setChangingFileId('');
    }
  });

  const searchFiles = () =>
    files.filter((file) =>
      file.name.toLowerCase().includes(searchValue.toLowerCase())
    );

  const isTypePDF = selectedFile?.mimeType?.includes('pdf');
  // Images (incl. a ready HEIC→JPEG preview, or one still pending) are previewed
  // inline; genuinely unsupported files fall back to the empty placeholder.
  const isTypeImage =
    !isTypePDF &&
    (isRenderableImageMimeType(selectedFile?.mimeType) ||
      isPreviewReady(selectedFile) ||
      isPreviewPending(selectedFile));
  const previewSrc = resolvePreviewSrc(selectedFile);
  const showImage = isTypeImage && !!previewSrc && !previewError;
  useEffect(() => setPreviewError(false), [previewSrc]);

  const handleUploadFile = (options: UploadRequestOption) => {
    const formData = new FormData();
    if (typeof options.file === 'object') {
      formData.append('file', options.file);
      if ('name' in options.file) {
        formData.append('name', options.file.name);
      }
    }
    formData.append('type', 'legal');
    formData.append('personId', companyId!);

    createFile(
      {
        parameter: {
          companyId: companyId!,
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        requestBody: formData as any,
      },
      {
        onSuccess: (res) => {
          setFiles(
            files?.length
              ? [...files, res].sort((a, b) => a.name.localeCompare(b.name))
              : [res]
          );
          setSelectedFile(res);
        },
      }
    );
  };

  const handleUpdateFile = (name: string) => {
    if (selectedFile) {
      updateFile(
        {
          parameter: {
            companyId: companyId!,
            id: selectedFile.id,
          },
          requestBody: {
            name,
          },
        },
        {
          onSuccess: (res) => {
            setChangingFileId('');
            setFiles((prev) =>
              prev
                .map((el) => (el.id === res.id ? res : el))
                .sort((a, b) => a.name.localeCompare(b.name))
            );
            message.success(`File ${name} was saved`);
          },
        }
      );
    }
  };

  const handleRemoveFile = () => {
    if (selectedFile) {
      deleteFile(
        {
          parameter: {
            companyId: companyId!,
            id: selectedFile.id,
          },
        },
        {
          onSuccess: (res) => {
            setFiles((prev) => prev.filter((el) => el.id !== res.id));
            setSelectedFile(null);
            setIsRemoveModalOpen(false);
            message.success('The document has been deleted');
          },
        }
      );
    }
  };

  // Selecting a row updates both the local selection (drives the preview and the
  // edit/delete actions) and the URL, so the current document is deeplinkable and
  // shareable — mirrors the Payslips list. `externalCompanyId` embeds this list in
  // other pages, so only push a legal deeplink for the standalone Legal route.
  const handleSelectFile = (companyFile: Schemas.CompanyFile) => {
    setSelectedFile(companyFile);
    if (!externalCompanyId) {
      navigate(`/companies/${companyId}/legal/${companyFile.id}`);
    }
  };

  const handleChangeVisible = (
    value: boolean,
    companyFile: Schemas.CompanyFile
  ) => {
    setIsActionsOpen(value);
    setSelectedFile(companyFile);
  };

  const handleEditFile = (id: string) => {
    setChangingFileId(id);
    setIsActionsOpen(false);
  };

  const handleOpenRemoveModal = () => {
    setIsRemoveModalOpen(true);
    setIsActionsOpen(false);
  };

  const actionsLoading =
    createFileLoading || updateFileLoading || deleteFileLoading;

  return (
    <Flex gap={14} ref={ref} vertical>
      {title}

      <Flex gap={28} vertical>
        <Flex justify="space-between" align="center">
          <SearchControl
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <Upload
            customRequest={handleUploadFile}
            beforeUpload={() => {}}
            showUploadList={false}
          >
            <S.UploadButton
              type="default"
              size="small"
              loading={createFileLoading}
              disabled={createFileLoading}
              icon={<IconUpload />}
            >
              Upload document
            </S.UploadButton>
          </Upload>
        </Flex>

        <Flex gap={30}>
          {searchFiles()?.length ? (
            <Flex flex={1} gap={12} vertical>
              {searchFiles()?.map((companyFile) => (
                <UploadFileItem
                  key={companyFile?.id}
                  isFileChanging={changingFileId === companyFile.id}
                  onFileNameSave={handleUpdateFile}
                  url={companyFile.url!}
                  name={companyFile.name}
                  loading={uploadStatus.isLoading}
                  onSelect={() => handleSelectFile(companyFile)}
                  isSelected={companyFile?.id === selectedFile?.id}
                  documentTypeLabel={getDocumentTypeLabel(companyFile.documentType)}
                  additionalIcon={
                    companyFile?.id === selectedFile?.id &&
                    updateFileLoading ? (
                      <Loader />
                    ) : (
                      <Popover
                        visible={
                          isActionsOpen && companyFile?.id === selectedFile?.id
                        }
                        onVisibleChange={(value) =>
                          handleChangeVisible(value, companyFile)
                        }
                        trigger="click"
                        placement="bottomRight"
                        arrow={false}
                        overlayClassName="ui-popover-legal-actions"
                        title={
                          <S.Actions>
                            <S.Edit
                              onClick={() => handleEditFile(companyFile?.id)}
                            >
                              <IconEdit />
                              <span>Change name</span>
                            </S.Edit>
                            <S.Remove onClick={handleOpenRemoveModal}>
                              <IconTrash />
                              <span>Delete</span>
                            </S.Remove>
                          </S.Actions>
                        }
                      >
                        <S.MoreIcon>
                          <IconMoreVertical />
                        </S.MoreIcon>
                      </Popover>
                    )
                  }
                />
              ))}
            </Flex>
          ) : (
            <Flex flex={1}>
              <S.EmptyList align="center" justify="center" gap={32} vertical>
                {getFilesLoading ? (
                  <Loader />
                ) : (
                  <>
                    <Flex gap={7} justify="center" align="center" vertical>
                      <span>There are no company documents yet</span>
                      <span>
                        You need to add your documents to display them here
                      </span>
                    </Flex>
                    <Upload
                      customRequest={handleUploadFile}
                      beforeUpload={() => {}}
                      showUploadList={false}
                    >
                      <S.UploadButton
                        type="default"
                        size="small"
                        loading={createFileLoading}
                        disabled={createFileLoading}
                        icon={<IconUpload />}
                      >
                        Upload document
                      </S.UploadButton>
                    </Upload>
                  </>
                )}
              </S.EmptyList>
            </Flex>
          )}
          <S.EmptyPreview>
            <Card title="Preview" minHeight={554}>
              {!!selectedFile && isTypePDF ? (
                <FileViewer
                  selectedFile={selectedFile}
                  loading={actionsLoading}
                  width="437px"
                  height="622px"
                />
              ) : actionsLoading ? (
                <Loader />
              ) : isTypeImage ? (
                isPreviewPending(selectedFile) ? (
                  <Loader />
                ) : showImage ? (
                  <img
                    src={previewSrc}
                    alt={selectedFile?.name}
                    onError={() => setPreviewError(true)}
                    style={{
                      maxWidth: '100%',
                      maxHeight: 622,
                      objectFit: 'contain',
                      borderRadius: 12,
                    }}
                  />
                ) : (
                  <EmptyDocumentPreview unsupported />
                )
              ) : (
                <EmptyDocumentPreview unsupported={!!selectedFile} />
              )}
            </Card>
          </S.EmptyPreview>
        </Flex>
      </Flex>
      <Modal open={isRemoveModalOpen} closeIcon={null} footer={null} centered>
        <Flex gap={24} vertical>
          <Flex gap={9} vertical>
            <S.ModalTitle>
              Are you sure you want to delete this document?
            </S.ModalTitle>
            <span>Once deleted, you will not be able to restore it</span>
          </Flex>

          <Flex justify="flex-end" gap={12}>
            <S.ModalButton
              isCancel
              type="default"
              onClick={() => setIsRemoveModalOpen(false)}
            >
              Cancel
            </S.ModalButton>
            <S.ModalButton
              type="primary"
              loading={deleteFileLoading}
              disabled={deleteFileLoading}
              onClick={handleRemoveFile}
            >
              Delete
            </S.ModalButton>
          </Flex>
        </Flex>
      </Modal>
    </Flex>
  );
};

export default LegalContent;
